import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { createImageUrl, uploadImage } from "services/createTripService";

interface UploadParams {
  file: File;
  folderName: string;
  fileName: string;
  url?: string;
  mimetype?: string;
}

// ✅ Step 1: Get Pre-signed URL
export const createUploadURL = createAsyncThunk(
  "images/createUploadURL",
  async ({ folderName, fileName }: { folderName: string; fileName: string }, thunkAPI) => {
    try {
      const response = await createImageUrl(folderName, fileName);
      return response; // Should return the upload URL
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to create upload URL");
    }
  }
);

// ✅ Step 2: Upload Image to S3
export const uploadImages = createAsyncThunk(
  "images/upload",
  async ({ file, url,mimetype }: Partial<UploadParams>, thunkAPI) => {
    try {
      const response = await uploadImage(file as File, url as string,mimetype as string);
      if(response.status===200){
        return {
          status:200,
          message:"Image uploaded successfully"
        }
      }else{
        return {
          status:500,
          message:"Failed to upload image"
        }
      }
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to upload image");
    }
  }
);

export const uploadSingleImage = createAsyncThunk(
  "images/uploadSingle",
  async ({ file, folderName, fileName, mimetype }: UploadParams, thunkAPI) => {
    try {
      // 1️⃣ Get Pre-signed URL
      const uploadUrlResponse = await thunkAPI.dispatch(createUploadURL({ folderName, fileName })).unwrap();

      if (uploadUrlResponse===null || !uploadUrlResponse.uploadURL) {
        throw new Error("Failed to get pre-signed URL");
      }
      // 2️⃣ Upload Image
      const url = uploadUrlResponse.uploadURL as string;
      await thunkAPI.dispatch(uploadImages({ file, url, mimetype })).unwrap();
      
      return {
        fileName,
        mimetype: mimetype,
        preview: `https://d3thbee5ims1p2.cloudfront.net/${folderName}/${fileName}`,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to upload image");
    }
  }
);

// ✅ Step 4: Batch Upload (Multiple Images)
export const uploadMultipleImages = createAsyncThunk(
  "images/uploadMultiple",
  async ({ files }: { files: UploadParams[] }, thunkAPI) => {
    const results = await Promise.allSettled(
      files.map((file) => {
        const { fileName, folderName, mimetype, file: data } = file;
        return thunkAPI.dispatch(
          uploadSingleImage({ file: data, folderName, fileName, mimetype })
        ).unwrap();
      })
    );

    // Proper type guards using `.status` from PromiseSettledResult<T>
    const successfulUploads = results
      .filter((r): r is PromiseFulfilledResult<any> => r.status === "fulfilled")
      .map((r) => r.value);

    const failedUploads = results
      .filter((r): r is PromiseRejectedResult => r.status === "rejected");

    if (failedUploads.length > 0) {
      toast.warn(`${failedUploads.length} image(s) failed to upload.`);
    }

    if (successfulUploads.length === 0) {
      return thunkAPI.rejectWithValue("All image uploads failed.");
    }

    return successfulUploads;
  }
);
